import React, { FC } from "react";
import { useNavigate } from "react-router-dom";
import { IVideo } from "../hooks/useFetchVideos";
import { IBrand } from "../../../brand/team/data/type";
import { VideoCardMenu } from "../../../../components/videoSection/VideoCardMenu";
import { StatusPill, FlagChip } from "../../../../components/videoSection/StatusPill";
import { getVideoStatus, isDemo, isNonProject } from "../../../../components/videoSection/videoStatus";
import { IconGoToTab } from "../../../../utils/icons";
import { DownloadBtn } from "./DownloadBtn";
import { formatName } from "./helpers";

interface Props {
  /** The video's project (brands' pre-approval setting for the Download gate). */
  project?: any;
  video: IVideo;
  onClose?: () => void;
  menuProps: {
    brand: IBrand;
    refetch: () => void;
    onDeleted?: () => void;
    onSettingsClosed?: () => void;
  };
}

/**
 * The nested-host header (handoff §04).
 *
 * On the expert profile and product detail this page renders INSIDE a page that
 * already owns a title and a breadcrumb. There the trail, the 28px title and
 * the page-level sticky strip are all suppressed, and identity collapses into
 * this single 44px row.
 *
 * It is INLINE at the top of the module and never pinned — a strip fixed under
 * the top bar would cover the host page's own content.
 */
export const NestedModuleBar: FC<Props> = ({ video, project, onClose, menuProps }) => {
  const navigate = useNavigate();
  const projectId = video.projectId ?? video.project?.id;
  const title = formatName(video);
  const status = getVideoStatus(video);

  return (
    <div className="flex items-center gap-2.5 px-4 border-b border-border-token bg-surface min-h-[44px] py-2 flex-wrap">
      {/* Present here too: the host's breadcrumb belongs to the profile, and
          back means "close ?video= and return to the grid in place". */}
      {onClose && (
        <button
          type="button"
          aria-label="Back"
          onClick={onClose}
          className="w-7 h-7 shrink-0 border border-border-token rounded-lg bg-surface text-text-2 inline-flex items-center justify-center hover:text-text-1 hover:border-accent transition-colors"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m14 6-6 6 6 6" />
          </svg>
        </button>
      )}
      <span
        className="font-CircularStdBlack text-text-1 truncate min-w-0"
        style={{ fontSize: 15, lineHeight: 1.2 }}
        title={title}
      >
        {title}
      </span>
      <StatusPill status={status.key} label={status.label} />
      {isDemo(video) && <FlagChip gold label="Demo" />}
      {isNonProject(video) && <FlagChip label="Non-project" />}

      <span className="flex-1 min-w-[8px]" />

      <div className="flex items-center gap-2 shrink-0">
        {projectId && (
          <button
            type="button"
            onClick={() => navigate(`/video-production/projects/${projectId}?tab=downloads`)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-[10px] bg-surface-2 text-text-1 hover:bg-track transition-colors"
            style={{ fontSize: 12.5 }}
          >
            <IconGoToTab width={14} height={14} stroke="currentColor" />
            View in Project
          </button>
        )}
        <DownloadBtn video={video} project={project || undefined} />
        <VideoCardMenu
          video={video}
          refetch={menuProps.refetch}
          brand={menuProps.brand}
          title={title}
          onDeleted={menuProps.onDeleted}
          onSettingsClosed={menuProps.onSettingsClosed}
        />
      </div>
    </div>
  );
};

export default NestedModuleBar;
